import type { CheckboxFilterOption } from '../../../lib/filters/buildCheckboxFilterOptions';
import type { ExplorationVolume, ViewMode } from '../../../types/exploration';
import {
  buildActivationOptions,
  buildClusterOptions,
  buildDiskTypeOptions,
  buildInfrastructureOptions,
  buildInstanceIdOptions,
  buildNodeGroupOptions,
  buildRegionOptions,
  buildSupportFilterGroups,
  buildTagFilterOptions,
  buildVolumeIdOptions,
} from '../../../lib/filters/buildCheckboxFilterOptions';
import { hasVisibleFilterOptions } from '~/shared/components/filters/FilterCheckboxList';

export type SectionId =
  | 'activation'
  | 'support'
  | 'infrastructure'
  | 'cluster'
  | 'region'
  | 'nodeGroup'
  | 'diskType'
  | 'volumeId'
  | 'instanceId'
  | 'tags'
  | 'capacity';

export interface SectionOptions {
  activation: CheckboxFilterOption[];
  support: ReturnType<typeof buildSupportFilterGroups>;
  infrastructure: CheckboxFilterOption[];
  cluster: CheckboxFilterOption[];
  region: CheckboxFilterOption[];
  nodeGroup: CheckboxFilterOption[];
  diskType: CheckboxFilterOption[];
  volumeId: CheckboxFilterOption[];
  instanceId: CheckboxFilterOption[];
  tags: CheckboxFilterOption[];
}

export function buildSectionOptions(volumes: ExplorationVolume[]): SectionOptions {
  return {
    activation: buildActivationOptions(volumes),
    support: buildSupportFilterGroups(volumes),
    infrastructure: buildInfrastructureOptions(volumes),
    cluster: buildClusterOptions(volumes),
    region: buildRegionOptions(volumes),
    nodeGroup: buildNodeGroupOptions(volumes),
    diskType: buildDiskTypeOptions(volumes),
    volumeId: buildVolumeIdOptions(volumes),
    instanceId: buildInstanceIdOptions(volumes),
    tags: buildTagFilterOptions(volumes),
  };
}

/**
 * Which sections render, given the built options, the current view and the panel's global
 * search query.
 *
 * Two asymmetries here are intentional-as-observed and pinned by the test suite rather than
 * corrected (the refactor that extracted this is strictly behaviour-preserving):
 *  - `infrastructure`, `region` and `diskType` are gated on option count ONLY — a global query
 *    that matches nothing still leaves them showing every option.
 *  - visibility is computed against the GLOBAL query alone, while `FilterCheckboxList` renders
 *    using the merged global+section query, so a section can be visible and render "No results".
 */
export function computeSectionVisibility(
  options: SectionOptions,
  ctx: { view: ViewMode; query: string }
): Record<SectionId, boolean> {
  const q = ctx.query;
  const globalVisible = (opts: CheckboxFilterOption[]) =>
    !q.trim() || hasVisibleFilterOptions(opts, q);

  return {
    activation: ctx.view === 'activation' && options.activation.length > 0,
    support: ctx.view === 'exploration' && options.support.length > 0,
    infrastructure: options.infrastructure.length > 0,
    cluster: options.cluster.length > 0 && globalVisible(options.cluster),
    region: options.region.length > 0,
    nodeGroup: options.nodeGroup.length > 0 && globalVisible(options.nodeGroup),
    diskType: options.diskType.length > 0,
    volumeId: options.volumeId.length > 0 && globalVisible(options.volumeId),
    instanceId: options.instanceId.length > 0 && globalVisible(options.instanceId),
    tags: options.tags.length > 0 && globalVisible(options.tags),
    capacity: true,
  };
}
